import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { useGlobal } from "../context/GlobalContext";
import { usePermissions } from "../context/PermissionsContext";
import useDashboardPrefetch from "../hooks/useDashboardPrefetch";

// Shared admin shell: left navigation + topbar + scrollable content.
export default function AdminLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);

  // CLOSE THE MOBILE DRAWER WHENEVER THE ROUTE CHANGES.
  //
  // Tapping a nav row closed it, because Sidebar's own handler calls
  // onMobileClose. Browser Back did not: this layout does not remount between
  // admin routes, so `mobileOpen` survived the navigation and the overlay sat
  // on top of a page the operator had already moved to — with the previous
  // page's row still highlighted underneath it. On a phone, where Back is the
  // primary gesture, that is the common path rather than an edge case.
  //
  // Keyed on `pathname` alone: a tab or filter change rewrites the query string
  // constantly (most tabbed pages use setSearchParams with replace:true), and
  // reacting to those would fight any future in-drawer control.
  const { pathname } = useLocation();
  useEffect(() => { setMobileOpen(false); }, [pathname]);

  // ── WARM THE DASHBOARD FROM THE SHELL ──────────────────────────────────────
  // This layout is mounted on every admin page and does NOT remount as the
  // operator navigates, so the hook fires once per app load rather than once per
  // route change. That is what keeps an idle prefetch from becoming a request
  // per page view.
  //
  // Gated on permission: the dashboard payload is private financial data, and
  // an operator who cannot open the page must not have it fetched for them.
  const { from, to, selectedRouterId } = useGlobal();
  const { canPage, loading: permLoading } = usePermissions();
  useDashboardPrefetch({
    enabled: !permLoading,
    allowed: typeof canPage === "function" ? canPage("/dashboard") : false,
    routerId: /^d+$/.test(String(selectedRouterId || "").trim()) ? String(selectedRouterId).trim() : "",
    from,
    to,
  });

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      <Sidebar
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
        <Topbar onMenuClick={() => setMobileOpen((p) => !p)} />
        <Box
          component="main"
          sx={{
            flex: 1,
            minWidth: 0,
            // The shell defines the space beside the sidebar. Pages fill it — keep
            // this the ONLY horizontal page gutter so content never looks trapped
            // in a container-within-a-container. Small + safe on mobile.
            px: { xs: 1.25, md: 3 },
            py: { xs: 2, md: 2.5 },
            pb: { xs: 4, md: 5 },
            overflow: "auto",
          }}
        >
          {children}
        </Box>
      </Box>
    </Box>
  );
}
